import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { fetchQuickBooksAuditReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

const QuickBooksAuditReport = () => {
  const dispatch = useDispatch();
  
  // Default to previous month
  const defaultStartDate = format(startOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
  const defaultEndDate = format(endOfMonth(subMonths(new Date(), 1)), "yyyy-MM-dd");
  
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [clinicId, setClinicId] = useState("");

  const {
    data: auditData,
    loading,
    error,
  } = useSelector((state) => state.reports.quickBooksAudit || { data: null, loading: false, error: null });

  const data = auditData?.data || {};
  const summary = data.summary || {};

  // Load data when component mounts
  useEffect(() => {
    dispatch(fetchQuickBooksAuditReport({ startDate, endDate, clinicId: clinicId || null }));
  }, [dispatch]);

  const handleRunAudit = () => {
    dispatch(fetchQuickBooksAuditReport({ startDate, endDate, clinicId: clinicId || null }));
  };

  const handleDownload = () => {
    if (!data || Object.keys(data).length === 0) {
      toast.warning("No audit data available to download.");
      return;
    }

    // Create workbook with multiple sheets
    const workbook = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = [
      ["QuickBooks Audit Report"],
      ["Period", `${startDate} to ${endDate}`],
      [""],
      ["System Counts"],
      ["Invoices", summary.systemCounts?.invoices || 0],
      ["Payments", summary.systemCounts?.payments || 0],
      [""],
      ["QuickBooks Counts"],
      ["Invoices", summary.qboCounts?.invoices || 0],
      ["Payments", summary.qboCounts?.payments || 0],
      [""],
      ["Discrepancies"],
      ["Invoice Discrepancies", summary.discrepancyCounts?.invoiceDiscrepancies || 0],
      ["Payment Discrepancies", summary.discrepancyCounts?.paymentDiscrepancies || 0],
      ["Missing Invoices in QB", summary.discrepancyCounts?.missingInvoicesInQbo || 0],
      ["Missing Invoices in System", summary.discrepancyCounts?.missingInvoicesInSystem || 0],
      ["Missing Payments in QB", summary.discrepancyCounts?.missingPaymentsInQbo || 0],
      ["Missing Payments in System", summary.discrepancyCounts?.missingPaymentsInSystem || 0],
      ["Voided Invoices Not Synced", summary.discrepancyCounts?.voidedInvoicesNotSynced || 0],
      ["Voided Payments Not Synced", summary.discrepancyCounts?.voidedPaymentsNotSynced || 0],
      ["Deleted Invoices Not Synced", summary.discrepancyCounts?.deletedInvoicesNotSynced || 0],
      ["Deleted Payments Not Synced", summary.discrepancyCounts?.deletedPaymentsNotSynced || 0],
      ["Payment-Invoice Mismatches", summary.discrepancyCounts?.paymentInvoiceRelationshipMismatches || 0],
      ["Payment Posting Errors", summary.discrepancyCounts?.paymentPostingErrors || 0],
      [""],
      ["Connection Status"],
      ["Total Connections", summary.connectionStatus?.totalConnections || 0],
      ["Connected", summary.connectionStatus?.connectedCount || 0],
      ["Issues", summary.connectionStatus?.issueCount || 0],
    ];
    
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    // Invoice Discrepancies sheet
    if (data.invoiceDiscrepancies && data.invoiceDiscrepancies.length > 0) {
      const invoiceDiscData = data.invoiceDiscrepancies.map((disc) => ({
        "Type": disc.type,
        "Invoice ID": disc.invoiceId,
        "Client": disc.clientName,
        "Clinic": disc.clinicName,
        "System Amount": disc.systemAmount,
        "QBO Amount": disc.qboAmount,
        "Difference": disc.difference,
        "Service Date": disc.serviceDate ? format(new Date(disc.serviceDate), "yyyy-MM-dd") : "",
        "QBO ID": disc.qboId || ""
      }));
      
      const invoiceDiscSheet = XLSX.utils.json_to_sheet(invoiceDiscData);
      XLSX.utils.book_append_sheet(workbook, invoiceDiscSheet, "Invoice Discrepancies");
    }

    // Payment Discrepancies sheet
    if (data.paymentDiscrepancies && data.paymentDiscrepancies.length > 0) {
      const paymentDiscData = data.paymentDiscrepancies.map((disc) => ({
        "Type": disc.type,
        "Payment ID": disc.paymentId,
        "Client": disc.clientName,
        "Clinic": disc.clinicName,
        "System Amount": disc.systemAmount,
        "QBO Amount": disc.qboAmount,
        "Difference": disc.difference,
        "Payment Date": disc.paymentDate ? format(new Date(disc.paymentDate), "yyyy-MM-dd") : "",
        "Payment Method": disc.paymentMethod,
        "Invoice Numbers": disc.invoiceNumbers,
        "QBO Payment ID": disc.qboPaymentId,
        "Description": disc.description
      }));
      
      const paymentDiscSheet = XLSX.utils.json_to_sheet(paymentDiscData);
      XLSX.utils.book_append_sheet(workbook, paymentDiscSheet, "Payment Discrepancies");
    }

    // Missing Invoices in QB sheet
    if (data.missingInvoicesInQbo && data.missingInvoicesInQbo.length > 0) {
      const missingInvoicesData = data.missingInvoicesInQbo.map((invoice) => ({
        "Invoice ID": invoice.invoiceId,
        "Client": invoice.clientName,
        "Clinic": invoice.clinicName,
        "Amount": invoice.amount,
        "Service Date": invoice.serviceDate ? format(new Date(invoice.serviceDate), "yyyy-MM-dd") : "",
        "QBO Posting Status": invoice.qboPostingStatus,
        "QBO Invoice ID": invoice.qboInvoiceId || "None"
      }));
      
      const missingInvoicesSheet = XLSX.utils.json_to_sheet(missingInvoicesData);
      XLSX.utils.book_append_sheet(workbook, missingInvoicesSheet, "Missing Invoices in QB");
    }

    // Missing Payments in QB sheet
    if (data.missingPaymentsInQbo && data.missingPaymentsInQbo.length > 0) {
      const missingPaymentsData = data.missingPaymentsInQbo.map((payment) => ({
        "Payment ID": payment.id,
        "Client": payment.clientName,
        "Clinic": payment.clinicName,
        "Amount": payment.amount,
        "Payment Date": payment.paymentDate ? format(new Date(payment.paymentDate), "yyyy-MM-dd") : "",
        "Payment Method": payment.paymentMethod,
        "Invoice Numbers": payment.invoiceNumbers,
        "QBO Posting Status": payment.qboPostingStatus,
        "QBO Payment ID": payment.qboPaymentId || "None",
        "Description": payment.description
      }));
      
      const missingPaymentsSheet = XLSX.utils.json_to_sheet(missingPaymentsData);
      XLSX.utils.book_append_sheet(workbook, missingPaymentsSheet, "Missing Payments in QB");
    }

    // Voided Invoices Not Synced sheet
    if (data.voidedInvoicesNotSynced && data.voidedInvoicesNotSynced.length > 0) {
      const voidedInvoicesData = data.voidedInvoicesNotSynced.map((invoice) => ({
        "Invoice ID": invoice.invoiceId,
        "Client": invoice.clientName,
        "Clinic": invoice.clinicName,
        "Amount": invoice.amount,
        "Service Date": invoice.serviceDate ? format(new Date(invoice.serviceDate), "yyyy-MM-dd") : "",
        "Voided Date": invoice.voidedDate ? format(new Date(invoice.voidedDate), "yyyy-MM-dd") : "",
        "QBO Status": invoice.qboStatus,
        "QBO Invoice ID": invoice.qboInvoiceId || "None"
      }));
      
      const voidedInvoicesSheet = XLSX.utils.json_to_sheet(voidedInvoicesData);
      XLSX.utils.book_append_sheet(workbook, voidedInvoicesSheet, "Voided Invoices Not Synced");
    }

    // Voided Payments Not Synced sheet
    if (data.voidedPaymentsNotSynced && data.voidedPaymentsNotSynced.length > 0) {
      const voidedPaymentsData = data.voidedPaymentsNotSynced.map((payment) => ({
        "Payment ID": payment.id,
        "Client": payment.clientName,
        "Clinic": payment.clinicName,
        "Amount": payment.amount,
        "Payment Date": payment.paymentDate ? format(new Date(payment.paymentDate), "yyyy-MM-dd") : "",
        "Voided Date": payment.voidedDate ? format(new Date(payment.voidedDate), "yyyy-MM-dd") : "",
        "Payment Method": payment.paymentMethod,
        "QBO Status": payment.qboStatus,
        "QBO Payment ID": payment.qboPaymentId || "None"
      }));
      
      const voidedPaymentsSheet = XLSX.utils.json_to_sheet(voidedPaymentsData);
      XLSX.utils.book_append_sheet(workbook, voidedPaymentsSheet, "Voided Payments Not Synced");
    }

    // Deleted Invoices Not Synced sheet
    if (data.deletedInvoicesNotSynced && data.deletedInvoicesNotSynced.length > 0) {
      const deletedInvoicesData = data.deletedInvoicesNotSynced.map((invoice) => ({
        "Invoice ID": invoice.invoiceId,
        "Client": invoice.clientName,
        "Clinic": invoice.clinicName,
        "Amount": invoice.amount,
        "Service Date": invoice.serviceDate ? format(new Date(invoice.serviceDate), "yyyy-MM-dd") : "",
        "QBO Status": invoice.qboStatus,
        "QBO Amount": invoice.qboAmount,
        "QBO Invoice ID": invoice.qboInvoiceId || "None",
        "Description": invoice.description
      }));
      
      const deletedInvoicesSheet = XLSX.utils.json_to_sheet(deletedInvoicesData);
      XLSX.utils.book_append_sheet(workbook, deletedInvoicesSheet, "Deleted Invoices Not Synced");
    }

    // Deleted Payments Not Synced sheet
    if (data.deletedPaymentsNotSynced && data.deletedPaymentsNotSynced.length > 0) {
      const deletedPaymentsData = data.deletedPaymentsNotSynced.map((payment) => ({
        "Payment ID": payment.paymentId,
        "Client": payment.clientName,
        "Clinic": payment.clinicName,
        "Amount": payment.amount,
        "Payment Date": payment.paymentDate ? format(new Date(payment.paymentDate), "yyyy-MM-dd") : "",
        "Payment Method": payment.paymentMethod,
        "QBO Status": payment.qboStatus,
        "QBO Amount": payment.qboAmount,
        "QBO Payment ID": payment.qboPaymentId || "None",
        "Invoice Numbers": payment.invoiceNumbers,
        "Description": payment.description
      }));
      
      const deletedPaymentsSheet = XLSX.utils.json_to_sheet(deletedPaymentsData);
      XLSX.utils.book_append_sheet(workbook, deletedPaymentsSheet, "Deleted Payments Not Synced");
    }

    // Payment-Invoice Relationship Mismatches sheet
    if (data.paymentInvoiceRelationshipMismatches && data.paymentInvoiceRelationshipMismatches.length > 0) {
      const relationshipMismatchData = data.paymentInvoiceRelationshipMismatches.map((payment) => ({
        "Payment ID": payment.paymentId,
        "Client": payment.clientName,
        "Clinic": payment.clinicName,
        "System Amount": payment.systemAmount,
        "QBO Amount": payment.qboAmount,
        "Payment Date": payment.paymentDate ? format(new Date(payment.paymentDate), "yyyy-MM-dd") : "",
        "Payment Method": payment.paymentMethod,
        "QBO Payment ID": payment.qboPaymentId,
        "System Invoices": payment.systemInvoices,
        "Expected QB Invoice IDs": payment.expectedQbInvoiceIds,
        "Actual QB Invoice IDs": payment.actualQbInvoiceIds,
        "Description": payment.description
      }));
      
      const relationshipMismatchSheet = XLSX.utils.json_to_sheet(relationshipMismatchData);
      XLSX.utils.book_append_sheet(workbook, relationshipMismatchSheet, "Payment-Invoice Mismatches");
    }

    // Payment Posting Errors sheet
    if (data.paymentPostingErrors && data.paymentPostingErrors.length > 0) {
      const postingErrorData = data.paymentPostingErrors.map((payment) => ({
        "Payment ID": payment.paymentId,
        "Client": payment.clientName,
        "Clinic": payment.clinicName,
        "Amount": payment.amount,
        "Payment Date": payment.paymentDate ? format(new Date(payment.paymentDate), "yyyy-MM-dd") : "",
        "Payment Method": payment.paymentMethod,
        "QB Posting Error": payment.qboPostingError,
        "Last Attempted": payment.qboLastAttemptedAt ? format(new Date(payment.qboLastAttemptedAt), "yyyy-MM-dd HH:mm") : "",
        "Invoice Numbers": payment.invoiceNumbers,
        "Description": payment.description
      }));
      
      const postingErrorSheet = XLSX.utils.json_to_sheet(postingErrorData);
      XLSX.utils.book_append_sheet(workbook, postingErrorSheet, "Payment Posting Errors");
    }

    // Save the file
    const fileName = `quickbooks-audit-${startDate}-to-${endDate}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const getTotalIssues = () => {
    if (!summary.discrepancyCounts) return 0;
    return (
      (summary.discrepancyCounts.invoiceDiscrepancies || 0) +
      (summary.discrepancyCounts.paymentDiscrepancies || 0) +
      (summary.discrepancyCounts.missingInvoicesInQbo || 0) +
      (summary.discrepancyCounts.missingInvoicesInSystem || 0) +
      (summary.discrepancyCounts.missingPaymentsInQbo || 0) +
      (summary.discrepancyCounts.missingPaymentsInSystem || 0) +
      (summary.discrepancyCounts.voidedInvoicesNotSynced || 0) +
      (summary.discrepancyCounts.voidedPaymentsNotSynced || 0) +
      (summary.discrepancyCounts.deletedInvoicesNotSynced || 0) +
      (summary.discrepancyCounts.deletedPaymentsNotSynced || 0) +
      (summary.discrepancyCounts.paymentInvoiceRelationshipMismatches || 0) +
      (summary.discrepancyCounts.paymentPostingErrors || 0)
    );
  };

  const renderDiscrepancySection = (title, discrepancies, type) => {
    if (!discrepancies || discrepancies.length === 0) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-lg font-semibold text-green-800 mb-2">{title}</h4>
          <p className="text-green-600">✅ No issues found</p>
        </div>
      );
    }

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-red-800 mb-2">
          {title} ({discrepancies.length})
        </h4>
        <div className="max-h-60 overflow-y-auto">
          {discrepancies.slice(0, 10).map((item, index) => (
            <div key={index} className="bg-white p-3 mb-2 rounded border border-red-100">
              {type === "invoiceDisc" && (
                <>
                  <div className="font-medium text-red-800">
                    {item.type}: {item.invoiceId}
                  </div>
                  <div className="text-sm text-gray-600">
                    {item.clientName} | {item.clinicName}
                  </div>
                  {item.systemAmount && (
                    <div className="text-sm text-red-600">
                      System: ${item.systemAmount} | QBO: ${item.qboAmount} | Diff: ${item.difference}
                    </div>
                  )}
                </>
              )}
              {type === "paymentDisc" && (
                <>
                  <div className="font-medium text-red-800">
                    Payment Amount Mismatch
                  </div>
                  <div className="text-sm text-gray-700 font-medium">
                    {item.clientName} | {item.clinicName}
                  </div>
                  <div className="text-sm text-gray-600">
                    Payment Date: {item.paymentDate ? format(new Date(item.paymentDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Method: {item.paymentMethod} | Invoices: {item.invoiceNumbers}
                  </div>
                  <div className="text-sm text-red-600 font-medium">
                    System: ${item.systemAmount} | QBO: ${item.qboAmount} | Difference: ${item.difference}
                  </div>
                  {item.description && (
                    <div className="text-xs text-gray-500 mt-1 italic">
                      {item.description}
                    </div>
                  )}
                </>
              )}
              {type === "missingInvoices" && (
                <>
                  <div className="font-medium text-red-800">
                    Invoice: {item.invoiceId}
                  </div>
                  <div className="text-sm text-gray-600">
                    {item.clientName} | {item.clinicName} | ${item.amount}
                  </div>
                  <div className="text-sm text-red-600">
                    Status: {item.qboPostingStatus}
                  </div>
                </>
              )}
              {type === "missingPayments" && (
                <>
                  <div className="font-medium text-red-800">
                    Missing Payment in QuickBooks
                  </div>
                  <div className="text-sm text-gray-700 font-medium">
                    {item.clientName} | {item.clinicName}
                  </div>
                  <div className="text-sm text-gray-600">
                    Payment Date: {item.paymentDate ? format(new Date(item.paymentDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Amount: ${item.amount} | Method: {item.paymentMethod}
                  </div>
                  <div className="text-sm text-gray-600">
                    Invoices: {item.invoiceNumbers}
                  </div>
                  <div className="text-sm text-red-600">
                    Status: {item.qboPostingStatus} | QBO ID: {item.qboPaymentId || 'None'}
                  </div>
                  {item.description && (
                    <div className="text-xs text-gray-500 mt-1 italic">
                      {item.description}
                    </div>
                  )}
                </>
              )}
              {type === "voidedInvoices" && (
                <>
                  <div className="font-medium text-red-800">
                    Voided Invoice: {item.invoiceId}
                  </div>
                  <div className="text-sm text-gray-600">
                    {item.clientName} | {item.clinicName} | ${item.amount}
                  </div>
                  <div className="text-sm text-red-600">
                    Void Status - System: Voided | QBO: {item.qboStatus}
                  </div>
                  <div className="text-sm text-gray-600">
                    Service Date: {item.serviceDate ? format(new Date(item.serviceDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                </>
              )}
              {type === "voidedPayments" && (
                <>
                  <div className="font-medium text-red-800">
                    Voided Payment Not Synced
                  </div>
                  <div className="text-sm text-gray-700 font-medium">
                    {item.clientName} | {item.clinicName}
                  </div>
                  <div className="text-sm text-gray-600">
                    Payment Date: {item.paymentDate ? format(new Date(item.paymentDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Amount: ${item.amount} | Method: {item.paymentMethod}
                  </div>
                  <div className="text-sm text-red-600">
                    Void Status - System: Voided | QBO: {item.qboStatus}
                  </div>
                </>
              )}
              {type === "deletedInvoices" && (
                <>
                  <div className="font-medium text-red-800">
                    Deleted Invoice: {item.invoiceId}
                  </div>
                  <div className="text-sm text-gray-600">
                    {item.clientName} | {item.clinicName}
                  </div>
                  <div className="text-sm text-red-600">
                    System: Deleted | QBO: {item.qboStatus} (${item.qboAmount})
                  </div>
                  <div className="text-sm text-gray-600">
                    Service Date: {item.serviceDate ? format(new Date(item.serviceDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 italic">
                    {item.description}
                  </div>
                </>
              )}
              {type === "deletedPayments" && (
                <>
                  <div className="font-medium text-red-800">
                    Deleted Payment Not Synced
                  </div>
                  <div className="text-sm text-gray-700 font-medium">
                    {item.clientName} | {item.clinicName}
                  </div>
                  <div className="text-sm text-gray-600">
                    Payment Date: {item.paymentDate ? format(new Date(item.paymentDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Amount: ${item.amount} | Method: {item.paymentMethod}
                  </div>
                  <div className="text-sm text-red-600">
                    System: Deleted | QBO: {item.qboStatus} (${item.qboAmount})
                  </div>
                  <div className="text-sm text-gray-600">
                    Invoices: {item.invoiceNumbers}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 italic">
                    {item.description}
                  </div>
                </>
              )}
              {type === "relationshipMismatch" && (
                <>
                  <div className="font-medium text-red-800">
                    Payment-Invoice Mismatch (QB Auto-linked Wrong)
                  </div>
                  <div className="text-sm text-gray-700 font-medium">
                    {item.clientName} | {item.clinicName}
                  </div>
                  <div className="text-sm text-gray-600">
                    Payment Date: {item.paymentDate ? format(new Date(item.paymentDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Amount: ${item.systemAmount} | Method: {item.paymentMethod}
                  </div>
                  <div className="text-sm text-red-600">
                    System Invoices: {item.systemInvoices}
                  </div>
                  <div className="text-sm text-red-600">
                    QB Linked To: Invoice IDs {item.actualQbInvoiceIds}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 italic">
                    QuickBooks automatically linked this payment to wrong invoice(s)
                  </div>
                </>
              )}
              {type === "postingErrors" && (
                <>
                  <div className="font-medium text-red-800">
                    Payment Posting Error
                  </div>
                  <div className="text-sm text-gray-700 font-medium">
                    {item.clientName} | {item.clinicName}
                  </div>
                  <div className="text-sm text-gray-600">
                    Payment Date: {item.paymentDate ? format(new Date(item.paymentDate), "yyyy-MM-dd") : "Unknown"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Amount: ${item.amount} | Method: {item.paymentMethod}
                  </div>
                  <div className="text-sm text-red-600 font-medium">
                    Error: {item.qboPostingError}
                  </div>
                  <div className="text-sm text-gray-500">
                    Last Attempt: {item.qboLastAttemptedAt ? format(new Date(item.qboLastAttemptedAt), "yyyy-MM-dd HH:mm") : "Unknown"}
                  </div>
                  <div className="text-sm text-gray-600">
                    Invoices: {item.invoiceNumbers}
                  </div>
                </>
              )}
            </div>
          ))}
          {discrepancies.length > 10 && (
            <div className="text-sm text-gray-500 mt-2">
              ... and {discrepancies.length - 10} more (see Excel export for full list)
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">QuickBooks Audit Report</h2>
        <button
          onClick={handleDownload}
          disabled={!data || Object.keys(data).length === 0}
          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
        >
          Download Excel Report
        </button>
      </div>

      {/* Filter Controls */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Clinic (Optional)
            </label>
            <input
              type="text"
              placeholder="Clinic ID"
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleRunAudit}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md"
            >
              {loading ? "Running Audit..." : "Run Audit"}
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          Error: {error.message || error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Running QuickBooks audit...</p>
        </div>
      )}

      {/* Audit Results */}
      {data && Object.keys(data).length > 0 && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Overall Status */}
            <div className={`p-6 rounded-lg shadow ${
              data.connectionIssues?.some(issue => issue.severity === 'critical') 
                ? 'bg-red-100 border border-red-300' 
                : getTotalIssues() === 0 
                  ? 'bg-green-100 border border-green-300' 
                  : 'bg-red-100 border border-red-300'
            }`}>
              <h3 className="text-lg font-semibold mb-2">Overall Status</h3>
              <div className={`text-2xl font-bold ${
                data.connectionIssues?.some(issue => issue.severity === 'critical')
                  ? 'text-red-600'
                  : getTotalIssues() === 0 
                    ? 'text-green-600' 
                    : 'text-red-600'
              }`}>
                {data.connectionIssues?.some(issue => issue.severity === 'critical') 
                  ? "🚨 Critical Connection Issues" 
                  : getTotalIssues() === 0 
                    ? "✅ All Clear" 
                    : `⚠️ ${getTotalIssues()} Issues`
                }
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Audit Period: {startDate} to {endDate}
              </p>
              {data.connectionIssues?.some(issue => issue.severity === 'critical') && (
                <p className="text-sm text-red-700 mt-2 font-medium">
                  QuickBooks connection expired - audit data may be incomplete
                </p>
              )}
            </div>

            {/* System vs QuickBooks Counts */}
            <div className="bg-blue-100 border border-blue-300 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-2 text-blue-800">Data Counts</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>System Invoices:</span>
                  <span className="font-medium">{summary.systemCounts?.invoices || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>QB Invoices:</span>
                  <span className="font-medium">{summary.qboCounts?.invoices || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>System Payments:</span>
                  <span className="font-medium">{summary.systemCounts?.payments || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>QB Payments:</span>
                  <span className="font-medium">{summary.qboCounts?.payments || 0}</span>
                </div>
              </div>
            </div>

            {/* Connection Status */}
            <div className="bg-purple-100 border border-purple-300 p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-2 text-purple-800">QB Connections</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span>Total:</span>
                  <span className="font-medium">{summary.connectionStatus?.totalConnections || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Connected:</span>
                  <span className="font-medium text-green-600">{summary.connectionStatus?.connectedCount || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Issues:</span>
                  <span className={`font-medium ${(summary.connectionStatus?.issueCount || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {summary.connectionStatus?.issueCount || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Issues */}
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-800">Detailed Analysis</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {renderDiscrepancySection(
                "Invoice Discrepancies",
                data.invoiceDiscrepancies,
                "invoiceDisc"
              )}
              
              {renderDiscrepancySection(
                "Payment Discrepancies", 
                data.paymentDiscrepancies,
                "paymentDisc"
              )}
              
              {renderDiscrepancySection(
                "Missing Invoices in QuickBooks",
                data.missingInvoicesInQbo,
                "missingInvoices"
              )}
              
              {renderDiscrepancySection(
                "Missing Payments in QuickBooks",
                data.missingPaymentsInQbo,
                "missingPayments"
              )}
              
              {renderDiscrepancySection(
                "Voided Invoices Not Synced to QuickBooks",
                data.voidedInvoicesNotSynced,
                "voidedInvoices"
              )}
              
              {renderDiscrepancySection(
                "Voided Payments Not Synced to QuickBooks",
                data.voidedPaymentsNotSynced,
                "voidedPayments"
              )}
              
              {renderDiscrepancySection(
                "Deleted Invoices Not Synced to QuickBooks",
                data.deletedInvoicesNotSynced,
                "deletedInvoices"
              )}
              
              {renderDiscrepancySection(
                "Deleted Payments Not Synced to QuickBooks",
                data.deletedPaymentsNotSynced,
                "deletedPayments"
              )}
              
              {renderDiscrepancySection(
                "Payment-Invoice Relationship Mismatches (QB Auto-linked Wrong)",
                data.paymentInvoiceRelationshipMismatches,
                "relationshipMismatch"
              )}
              
              {renderDiscrepancySection(
                "Payment Posting Errors (Failed to Post to QB)",
                data.paymentPostingErrors,
                "postingErrors"
              )}
            </div>

            {/* Connection Issues */}
            {data.connectionIssues && data.connectionIssues.length > 0 && (
              <div className={`border rounded-lg p-4 ${
                data.connectionIssues.some(issue => issue.severity === 'critical') 
                  ? 'bg-red-50 border-red-200' 
                  : 'bg-yellow-50 border-yellow-200'
              }`}>
                <h4 className={`text-lg font-semibold mb-2 ${
                  data.connectionIssues.some(issue => issue.severity === 'critical')
                    ? 'text-red-800'
                    : 'text-yellow-800'
                }`}>
                  {data.connectionIssues.some(issue => issue.severity === 'critical') ? '🚨 Critical ' : '⚠️ '}
                  Connection Issues ({data.connectionIssues.length})
                </h4>
                <div className="space-y-3">
                  {data.connectionIssues.map((issue, index) => (
                    <div key={index} className={`bg-white p-3 rounded border ${
                      issue.severity === 'critical' ? 'border-red-200' : 'border-yellow-100'
                    }`}>
                      <div className={`font-medium ${
                        issue.severity === 'critical' ? 'text-red-800' : 'text-yellow-800'
                      }`}>
                        {issue.type}: Clinic {issue.clinicId}
                      </div>
                      <div className="text-sm text-gray-700 mt-1">
                        {issue.message}
                      </div>
                      <div className={`text-sm mt-2 font-medium ${
                        issue.severity === 'critical' ? 'text-red-700' : 'text-yellow-700'
                      }`}>
                        Action Required: {issue.action}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">
                        Impact: {issue.impact}
                      </div>
                      {issue.expiresAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          {issue.type.includes('EXPIRED') ? 'Expired: ' : 'Expires: '}
                          {format(new Date(issue.expiresAt), "yyyy-MM-dd HH:mm")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {data.connectionIssues.some(issue => issue.severity === 'critical') && (
                  <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded">
                    <div className="text-red-800 font-medium text-sm">
                      ⚠️ Warning: Audit results may be incomplete due to critical connection issues.
                    </div>
                    <div className="text-red-700 text-sm mt-1">
                      Please resolve connection issues and re-run the audit for accurate results.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* No Data State */}
      {data && Object.keys(data).length === 0 && !loading && !error && (
        <div className="text-center py-8">
          <p className="text-gray-500">No audit data available. Click "Run Audit" to generate a report.</p>
        </div>
      )}
    </div>
  );
};

export default QuickBooksAuditReport;